// @ts-strict-ignore
import {Component, inject, Injector, OnInit, signal, WritableSignal} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {Translations} from "@translations";
import {
	AudioChannelGroup,
	AudioInputChannel,
	DynamicAssignedAudioChannelGroup,
	GetDynamicAudioInputChannelDataForRecorderResponse,
	RecorderMapping,
} from "@models";
import {TrackByMethods} from "src/app/shared/utilities/track-by-methods";
import {NotificationService, SimAudioChannelService, SimEnumExtendedService, SimSystemService} from "@services";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {TranslateService} from "@ngx-translate/core";
import {FormControl, Validators} from "@angular/forms";
import {MatSlideToggleChange} from "@angular/material/slide-toggle";
import {UntilDestroy} from "@ngneat/until-destroy";

interface GroupInfo {
	channels: Set<AudioInputChannel>;
	isActive: boolean;
}

export interface DialogData_DynamicAudioChannelConfigurationDialogComponentInput extends MatDialogDataBaseInput {
	recorderId: string;
}

export interface DialogData_DynamicAudioChannelConfigurationDialogComponentOutput extends MatDialogDataBaseOutput {}

@UntilDestroy()
@Component({
	selector: "sim-dynamic-audio-channel-configuration-dialog",
	templateUrl: "./dynamic-audio-channel-configuration-dialog.component.html",
	styleUrls: ["./dynamic-audio-channel-configuration-dialog.component.scss"],
	standalone: false,
})
export class DynamicAudioChannelConfigurationDialogComponent
	extends BasicMaterialDialog<
		DynamicAudioChannelConfigurationDialogComponent,
		DialogData_DynamicAudioChannelConfigurationDialogComponentInput,
		DialogData_DynamicAudioChannelConfigurationDialogComponentOutput
	>
	implements OnInit
{
	// Properties
	T = Translations;
	public recorderMapping: RecorderMapping;
	public finishedLoading: boolean;
	public hasUnsavedChanges: boolean;
	public groupNameFormCtrl = new FormControl<string>({value: null, disabled: true}, [Validators.maxLength(50)]);

	// Maps
	public groupMap = new Map<AudioChannelGroup, GroupInfo>();
	public unassignedChannels = new Set<AudioInputChannel>();

	//Signals
	public orderedGroups: WritableSignal<AudioChannelGroup[]> = signal([]);
	public editGroupId: WritableSignal<string | null> = signal(null);

	// Readonly
	protected readonly TrackByMethods = TrackByMethods;
	protected readonly AVAILABLE_DROP_LIST_ID: string = "availableDynamicInputChannels";

	// Service Injections
	private _simSystemService: SimSystemService = inject(SimSystemService);
	private _audioChannelService: SimAudioChannelService = inject(SimAudioChannelService);
	private _simNotificationService: NotificationService = inject(NotificationService);
	private _translate: TranslateService = inject(TranslateService);
	private _simEnumExtendedService: SimEnumExtendedService = inject(SimEnumExtendedService);

	constructor(_injector: Injector) {
		super(_injector);
	}

	ngOnInit(): void {
		this.fetchData();
	}

	public getChannelFontIcon(inputChannel: AudioInputChannel) {
		return !!inputChannel.icon ? this._simEnumExtendedService.getFontIcon(inputChannel.icon) : "fa-microphone";
	}

	public channelDroppedAssigned(event: CdkDragDrop<AudioInputChannel[]>, channelGroup: AudioChannelGroup): void {
		if (!event.isPointerOverContainer) return;
		const channel = event.item.data.channel as AudioInputChannel;
		if (event.previousContainer.id.startsWith(this.AVAILABLE_DROP_LIST_ID)) {
			// Move from unassigned to group
			this.unassignedChannels.delete(channel);
			this.groupMap.get(channelGroup).channels.add(channel);
			this.hasUnsavedChanges = true;
		} else if (!!event.item.data.previousGroup) {
			// Move from one group to another
			this.groupMap.get(event.item.data.previousGroup).channels.delete(channel);
			this.groupMap.get(channelGroup).channels.add(channel);
			this.hasUnsavedChanges = true;
		}
	}

	public channelDroppedUnassigned(event: CdkDragDrop<AudioInputChannel[]>): void {
		if (!event.isPointerOverContainer) return;
		const channel = event.item.data.channel as AudioInputChannel;
		const oldGroup = event.item.data.previousGroup as AudioChannelGroup;
		this.groupMap.get(oldGroup).channels.delete(channel);
		this.unassignedChannels.add(channel);
		this.hasUnsavedChanges = true;
	}

	public revertToDefault(): void {
		this._simSystemService
			.revertRecorderToDefaultDynamicInputs({
				recorderMappingId: this.data.recorderId,
			})
			.then(() => {
				this._simNotificationService.notifySuccess(
					this._translate.instant(this.T.DYNAMIC_AUDIO_CHANNEL_POPUP_MESSAGE_REVERT_DEFAULT_SUCCESS),
				);
				// Reset the component state
				this.hasUnsavedChanges = false;
				this.groupMap.clear();
				this.unassignedChannels.clear();
				this.orderedGroups.set([]);
				this.fetchData();
			})
			.catch(() =>
				this._simNotificationService.notifyError(
					this._translate.instant(this.T.DYNAMIC_AUDIO_CHANNEL_POPUP_MESSAGE_REVERT_DEFAULT_FAILURE),
				),
			);
	}

	public saveCurrentAsDefault(): void {
		this._simSystemService
			.setDefaultDynamicAudioInputPreset({
				recorderMappingId: this.data.recorderId,
				defaultDynamicAudioChannelGroups: this.recorderMapping.dynamicAudioChannelGroups,
			})
			.then(() =>
				this._simNotificationService.notifySuccess(
					this._translate.instant(this.T.DYNAMIC_AUDIO_CHANNEL_POPUP_MESSAGE_UPDATE_DEFAULT_SUCCESS),
				),
			)
			.catch(() =>
				this._simNotificationService.notifyError(
					this._translate.instant(this.T.DYNAMIC_AUDIO_CHANNEL_POPUP_MESSAGE_UPDATE_DEFAULT_FAILURE),
				),
			);
	}

	public doSave(): void {
		// Update static groups
		this._audioChannelService
			.upsertAudioChannelGroups(Array.from(this.groupMap.keys()))
			.catch(() =>
				this._simNotificationService.notifyError(
					this._translate.instant(this.T.DYNAMIC_AUDIO_CHANNEL_POPUP_MESSAGE_UPDATE_CHANNELS_FAILURE),
				),
			);

		// Update dynamic group mappings
		const dynamicChannelGroupsToSave = [] as DynamicAssignedAudioChannelGroup[];
		this.groupMap.forEach((mapping, group) => {
			if (mapping.isActive) {
				dynamicChannelGroupsToSave.push({
					audioChannelGroupId: group.id,
					dynamicAudioChannelIds: Array.from(mapping.channels).map((channel) => channel.id),
					dynamicAudioChannels: [],
				} as DynamicAssignedAudioChannelGroup);
			}
		});
		this._simSystemService
			.updateDynamicAudioInputChannelsForRecorder({
				recorderMappingId: this.data.recorderId,
				updatedDynamicAudiochannelGroups: dynamicChannelGroupsToSave,
			})
			.then(() => {
				this._simNotificationService.notifySuccess(
					this._translate.instant(this.T.DYNAMIC_AUDIO_CHANNEL_POPUP_MESSAGE_UPDATE_CHANNELS_SUCCESS),
				);
				this.close({action: "ok"});
			})
			.catch(() =>
				this._simNotificationService.notifyError(
					this._translate.instant(this.T.DYNAMIC_AUDIO_CHANNEL_POPUP_MESSAGE_UPDATE_CHANNELS_FAILURE),
				),
			);
	}

	public editChannelGroupName(channelGroup: AudioChannelGroup): void {
		this.groupNameFormCtrl.setValue(channelGroup.name);
		this.groupNameFormCtrl.enable();
		this.editGroupId.set(channelGroup.id);
	}

	public updateGroupName(channelGroup: AudioChannelGroup): void {
		const validatedNewName = this.groupNameFormCtrl.value.trim();
		if (!!validatedNewName && validatedNewName !== channelGroup.name) {
			channelGroup.name = validatedNewName;
			this.hasUnsavedChanges = true;
		}
		this.editGroupId.set(null);
		this.groupNameFormCtrl.disable();
	}

	public updateGroupOrder(channelGroup: AudioChannelGroup, direction: "up" | "down"): void {
		// Step 1: Convert the Map to a sorted array
		const groupArray = Array.from(this.groupMap.keys());

		// Step 2: Find the index of the channelGroup
		const index = groupArray.findIndex((group) => group === channelGroup);

		// Step 3: Swap the channelGroup with the previous or next element, if it exists
		if (direction === "up" && index > 0) {
			[groupArray[index - 1], groupArray[index]] = [groupArray[index], groupArray[index - 1]];
		} else if (direction === "down" && index < groupArray.length - 1) {
			[groupArray[index], groupArray[index + 1]] = [groupArray[index + 1], groupArray[index]];
		}

		// Step 4: Update the order property of each AudioChannelGroup
		groupArray.forEach((group: AudioChannelGroup, newIndex: number) => {
			group.order = newIndex;
		});

		// Step 5: Update the map & signal with the new order
		this.groupMap = new Map(groupArray.map((group) => [group, this.groupMap.get(group)]));
		this.orderedGroups.set(groupArray);
		this.hasUnsavedChanges = true;
	}

	public toggleGroupActive(channelGroup: AudioChannelGroup, changeEvent: MatSlideToggleChange): void {
		this.groupMap.get(channelGroup).channels.forEach((channel) => this.unassignedChannels.add(channel));
		this.groupMap.set(channelGroup, {isActive: changeEvent.checked, channels: new Set<AudioInputChannel>()});
		this.hasUnsavedChanges = true;
	}

	private fetchData(): void {
		this._simSystemService
			.getDynamicAudioInputChannelDataForRecorder(this.data.recorderId)
			.then((response: GetDynamicAudioInputChannelDataForRecorderResponse) => {
				if (!response) return;
				this.recorderMapping = response?.recorderMapping;

				// unpack channel groups
				response.channelGroups.forEach((channelGroup) => {
					const dynamicGroup = this.recorderMapping.dynamicAudioChannelGroups.find(
						(dynamicGroup) => dynamicGroup.audioChannelGroupId == channelGroup.id,
					);
					const assignedChannels = response.dynamicInputChannels.filter((channel) =>
						dynamicGroup?.dynamicAudioChannelIds.includes(channel.id),
					);
					this.groupMap.set(channelGroup, {isActive: !!dynamicGroup, channels: new Set(assignedChannels)});
				});
				this.orderedGroups.set(Array.from(this.groupMap.keys()));

				// unpack channels
				const assignedChannelIds = this.recorderMapping.dynamicAudioChannelGroups.flatMap((group) => group.dynamicAudioChannelIds);
				response.dynamicInputChannels.forEach((channel) => {
					if (!assignedChannelIds.includes(channel.id)) this.unassignedChannels.add(channel);
				});
				this.finishedLoading = true;
			});
	}
}
